import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  RefreshCw,
  Search,
  X,
  Clock,
  Activity,
  CheckCircle,
  XCircle,
  Copy,
  ExternalLink,
  Plus,
  Grid,
  List as ListIcon,
  Folder
} from 'react-feather';
import { useAuth } from '../context/AuthContext';
import { ordersAPI } from '../api';
import AdsDisplay from '../components/AdsDisplay';
import { formatCurrencyWithConversion } from '../utils/currency';
import PlatformIcon, { detectPlatform } from '../components/PlatformIcon';
import './Orders.css';

const STATUS_FILTERS = [
  { id: 'all', label: 'All Orders', icon: null },
  { id: 'pending', label: 'Pending', icon: Clock, color: '#D97706' },
  { id: 'in_progress', label: 'In Progress', icon: Activity, color: '#2563EB' },
  { id: 'completed', label: 'Completed', icon: CheckCircle, color: '#16A34A' },
  { id: 'partial', label: 'Partial', icon: AlertCircle, color: '#9333EA' },
  { id: 'canceled', label: 'Canceled', icon: XCircle, color: '#DC2626' },
];

export default function Orders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userCurrency = user?.currency || 'USD';

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'
  const [page, setPage] = useState(1);
  const [copiedLink, setCopiedLink] = useState(null);
  const pageSize = 20;

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // Use getRecent which returns user's active & historical orders
      const res = await ordersAPI.getRecent();
      setOrders(res.data.orders || []);
    } catch (err) {
      console.error('Fetch orders error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleCopyLink = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedLink(text);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  // Status counts for filter chips
  const counts = useMemo(() => {
    const c = { all: orders.length, pending: 0, in_progress: 0, completed: 0, partial: 0, canceled: 0 };
    orders.forEach((o) => {
      const s = (o.status || '').toLowerCase().replace(/[\s-]/g, '_');
      if (s === 'pending') c.pending++;
      else if (s === 'in_progress' || s === 'processing' || s === 'inprogress') c.in_progress++;
      else if (s === 'completed' || s === 'complete') c.completed++;
      else if (s === 'partial') c.partial++;
      else if (s === 'canceled' || s === 'cancelled' || s === 'failed') c.canceled++;
    });
    return c;
  }, [orders]);

  // Filtered orders list
  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const s = (o.status || '').toLowerCase().replace(/[\s-]/g, '_');

      // Status match
      if (activeFilter === 'pending' && s !== 'pending') return false;
      if (activeFilter === 'in_progress' && !(s === 'in_progress' || s === 'processing' || s === 'inprogress')) return false;
      if (activeFilter === 'completed' && !(s === 'completed' || s === 'complete')) return false;
      if (activeFilter === 'partial' && s !== 'partial') return false;
      if (activeFilter === 'canceled' && !(s === 'canceled' || s === 'cancelled' || s === 'failed')) return false;

      // Search match
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const idMatch = String(o.id || o.order_id || '').toLowerCase().includes(q);
        const nameMatch = (o.service_name || o.service || '').toLowerCase().includes(q);
        const linkMatch = (o.link || '').toLowerCase().includes(q);
        const catMatch = (o.category || '').toLowerCase().includes(q);
        if (!idMatch && !nameMatch && !linkMatch && !catMatch) return false;
      }

      return true;
    });
  }, [orders, activeFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
  const paginatedOrders = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, page, pageSize]);

  const renderStatusBadge = (status = '') => {
    const s = status.toLowerCase().replace(/[\s-]/g, '_');
    if (s === 'completed' || s === 'complete') {
      return (
        <span className="order-status-badge badge-done">
          <CheckCircle size={12} /> Completed
        </span>
      );
    }
    if (s === 'in_progress' || s === 'processing' || s === 'inprogress') {
      return (
        <span className="order-status-badge badge-active">
          <Activity size={12} className="spin-slow" /> In Progress
        </span>
      );
    }
    if (s === 'pending') {
      return (
        <span className="order-status-badge badge-wait">
          <Clock size={12} /> Pending
        </span>
      );
    }
    if (s === 'partial') {
      return (
        <span className="order-status-badge badge-part">
          <AlertCircle size={12} /> Partial
        </span>
      );
    }
    return (
      <span className="order-status-badge badge-fail">
        <XCircle size={12} /> Canceled
      </span>
    );
  };

  return (
    <div className="orders-page">
      {/* ── 1. Top Header ── */}
      <div className="orders-header-row">
        <div>
          <h1 className="page-title">My Orders</h1>
          <p className="page-subtitle">
            Track status, delivery progress, and campaign performance
          </p>
        </div>

        <div className="orders-header-actions">
          <button
            type="button"
            className="btn btn-secondary orders-refresh-btn"
            onClick={fetchOrders}
            disabled={loading}
          >
            <RefreshCw size={15} className={loading ? 'spin-icon' : ''} />
            <span>Refresh</span>
          </button>

          <Link to="/dashboard/orders/new" className="btn btn-primary orders-new-btn">
            <Plus size={16} />
            <span>New Order</span>
          </Link>
        </div>
      </div>

      <AdsDisplay adSlot="banner-300x250" className="medium-rectangle" />
      <AdsDisplay adSlot="banner-320x50" className="banner" />

      {/* ── 2. Filters & Controls Box ── */}
      <div className="card orders-filter-card">
        {/* Status Filter Chips (Matching Mobile App) */}
        <div className="orders-status-chips-row">
          {STATUS_FILTERS.map((tab) => {
            const isActive = activeFilter === tab.id;
            const Icon = tab.icon;
            const count = counts[tab.id] || 0;

            return (
              <button
                key={tab.id}
                type="button"
                className={`order-filter-chip ${isActive ? 'active' : ''}`}
                onClick={() => {
                  setActiveFilter(tab.id);
                  setPage(1);
                }}
              >
                {Icon && !isActive && (
                  <Icon size={14} style={{ color: tab.color, marginRight: 5 }} />
                )}
                <span>{tab.label}</span>
                <span className="order-filter-count">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Search Bar & View Mode Toggle */}
        <div className="orders-search-and-view">
          <div className="orders-search-box">
            <Search size={16} className="orders-search-icon" />
            <input
              type="text"
              className="orders-search-input"
              placeholder="Search by Order #ID, service name, or link URL..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            {search && (
              <button
                type="button"
                className="orders-search-clear"
                onClick={() => setSearch('')}
              >
                <X size={15} />
              </button>
            )}
          </div>

          <div className="orders-view-switchers">
            <button
              type="button"
              className={`orders-view-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Modern Cards View"
            >
              <Grid size={15} />
              <span>Cards</span>
            </button>
            <button
              type="button"
              className={`orders-view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Spreadsheet Table View"
            >
              <ListIcon size={15} />
              <span>Table</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── 3. Orders Content ── */}
      {loading ? (
        <div className="card orders-loading-card">
          <div className="spinner" />
          <p className="orders-loading-text">Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="card orders-empty-card">
          <div className="orders-empty-icon-circle">
            <AlertCircle size={36} />
          </div>
          <h3 className="orders-empty-title">No orders found</h3>
          <p className="orders-empty-subtitle">
            {search || activeFilter !== 'all'
              ? 'No orders matched your active filter or search keywords.'
              : "You haven't placed any orders yet. Start your first campaign to boost your social presence!"}
          </p>
          {search || activeFilter !== 'all' ? (
            <button
              type="button"
              className="btn btn-outline-hero"
              style={{ marginTop: 14 }}
              onClick={() => {
                setSearch('');
                setActiveFilter('all');
              }}
            >
              Reset Filters
            </button>
          ) : (
            <Link to="/dashboard/orders/new" className="btn btn-primary" style={{ marginTop: 14 }}>
              Place Your First Order
            </Link>
          )}
        </div>
      ) : viewMode === 'cards' ? (
        /* ── Modern Cards Grid (Matching Mobile OrderCard) ── */
        <div className="orders-cards-list">
          {paginatedOrders.map((o) => {
            const orderId = `#${o.id || o.order_id}`;
            const sName = o.service_name || o.service || 'Social Media Service';
            const cat = o.category || 'General';
            const totalQty = parseInt(o.quantity, 10) || 1000;
            const remains = o.remains !== undefined && o.remains !== null ? parseInt(o.remains, 10) : null;
            const deliveredQty =
              remains !== null
                ? Math.max(0, totalQty - remains)
                : o.status === 'completed'
                ? totalQty
                : 0;

            const progressPercent = Math.min(100, Math.max(0, Math.round((deliveredQty / totalQty) * 100)));
            const isCompleted = o.status === 'completed';
            const isCanceled = o.status === 'canceled' || o.status === 'cancelled';
            const barClass = isCompleted ? 'bar-green' : isCanceled ? 'bar-red' : 'bar-blue';

            return (
              <div key={o.id} className="order-modern-card card">
                {/* Header Row: Platform Icon + Service Name + Status */}
                <div className="order-card-header">
                  <div className="order-card-header-left">
                    <PlatformIcon platform={sName || cat} size={42} iconSize={22} />
                    <div className="order-header-text">
                      <div className="order-id-and-cat">
                        <span className="order-id-badge">{orderId}</span>
                        {cat && (
                          <span className="order-cat-tag">
                            <Folder size={10} /> {cat}
                          </span>
                        )}
                      </div>
                      <h4 className="order-service-name" title={sName}>{sName}</h4>
                    </div>
                  </div>

                  <div className="order-card-header-right">
                    {renderStatusBadge(o.status)}
                    <span className="order-date-text">
                      {new Date(o.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Metrics Row: Cost, Quantity, Start, Remains */}
                <div className="order-card-metrics-grid">
                  <div className="order-metric-box">
                    <span className="order-metric-label">Charge</span>
                    <span className="order-metric-val cost-val">
                      {formatCurrencyWithConversion(o.cost, userCurrency, 3)}
                    </span>
                  </div>

                  <div className="order-metric-box">
                    <span className="order-metric-label">Quantity</span>
                    <span className="order-metric-val">{totalQty.toLocaleString()}</span>
                  </div>

                  <div className="order-metric-box">
                    <span className="order-metric-label">Start Count</span>
                    <span className="order-metric-val">{o.start_count !== null && o.start_count !== undefined ? o.start_count : '—'}</span>
                  </div>

                  <div className="order-metric-box">
                    <span className="order-metric-label">Remains</span>
                    <span className="order-metric-val">{remains !== null ? remains.toLocaleString() : '—'}</span>
                  </div>
                </div>

                {/* Delivery Progress Bar */}
                <div className="order-progress-container">
                  <div className="order-progress-labels">
                    <span className="progress-label-text">Delivery Progress</span>
                    <span className="progress-ratio-text">
                      {deliveredQty.toLocaleString()} / {totalQty.toLocaleString()} ({progressPercent}%)
                    </span>
                  </div>
                  <div className="order-progress-bar-bg">
                    <div
                      className={`order-progress-bar-fill ${barClass}`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>

                {/* Target URL Box */}
                {o.link && (
                  <div className="order-target-link-box">
                    <span className="target-link-label">Target Link:</span>
                    <a
                      href={o.link}
                      target="_blank"
                      rel="noreferrer"
                      className="order-target-link"
                      title={o.link}
                    >
                      <ExternalLink size={13} />
                      <span className="link-url-text">{o.link}</span>
                    </a>
                    <button
                      type="button"
                      className="order-copy-link-btn"
                      onClick={() => handleCopyLink(o.link)}
                      title="Copy URL"
                    >
                      <Copy size={13} />
                      <span>{copiedLink === o.link ? 'Copied!' : 'Copy'}</span>
                    </button>
                  </div>
                )}

                {/* Footer: Refund Note + Order Again Action */}
                <div className="order-card-footer">
                  <div className="order-footer-left">
                    {o.refund_status === 'refunded' || o.refund_status === 'manual_refunded' ? (
                      <span className="order-refund-badge">
                        Refunded: +{formatCurrencyWithConversion(o.refund_amount, userCurrency, 2)}
                      </span>
                    ) : (
                      <span className="order-time-sub">
                        Placed at {new Date(o.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>

                  <Link
                    to={`/dashboard/orders/new?category=${encodeURIComponent(cat)}&service_id=${o.service_id}`}
                    className="order-again-btn"
                  >
                    <span>Order Again</span>
                    <span className="order-btn-arrow">›</span>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Spreadsheet Table View ── */
        <div className="card orders-table-card">
          <div className="table-wrap orders-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#ID</th>
                  <th>Service</th>
                  <th>Target Link</th>
                  <th>Quantity</th>
                  <th>Charge</th>
                  <th>Start</th>
                  <th>Remains</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((o) => (
                  <tr key={o.id}>
                    <td className="order-td-id">#{o.id}</td>
                    <td>
                      <div className="order-td-service">
                        <PlatformIcon platform={o.service_name || o.category} size={28} iconSize={15} />
                        <div>
                          <div className="order-td-name" title={o.service_name}>{o.service_name}</div>
                          <div className="order-td-cat">{o.category || 'General'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <a href={o.link} target="_blank" rel="noreferrer" className="order-table-link" title={o.link}>
                        {o.link}
                      </a>
                    </td>
                    <td className="font-semibold">{parseInt(o.quantity, 10).toLocaleString()}</td>
                    <td className="order-table-cost">{formatCurrencyWithConversion(o.cost, userCurrency, 3)}</td>
                    <td className="text-muted">{o.start_count !== null && o.start_count !== undefined ? o.start_count : '—'}</td>
                    <td className="text-muted">{o.remains !== null && o.remains !== undefined ? o.remains : '—'}</td>
                    <td>{renderStatusBadge(o.status)}</td>
                    <td className="order-table-date">
                      <div>{new Date(o.created_at).toLocaleDateString()}</div>
                      <div className="text-muted">{new Date(o.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                    <td>
                      <Link
                        to={`/dashboard/orders/new?category=${encodeURIComponent(o.category || '')}&service_id=${o.service_id}`}
                        className="order-table-again-link"
                      >
                        Reorder
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── 4. Pagination ── */}
      {totalPages > 1 && (
        <div className="orders-pagination">
          <button
            type="button"
            className="pagination-btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹ Prev
          </button>

          <span className="pagination-info">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </span>

          <button
            type="button"
            className="pagination-btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
}
